import { LineItemSchema } from '@/types/validationSchemas/LineItemSchema';
import { OpportunitySchema } from '@/types/validationSchemas/OpportunitySchema';
import { QuoteSchema } from '@/types/validationSchemas/QuoteSchema';
import { JobSchema } from '@/types/validationSchemas/JobSchema';
import { InvoiceSchema } from '@/types/validationSchemas/InvoiceSchema';
import { ProjectSchema } from '@/types/validationSchemas/ProjectSchema';
import { customerSchema } from '@/types/validationSchemas/customerSchema';
import { userSchema } from '@/types/validationSchemas/userSchema';
import { reminderSchema } from '@/types/validationSchemas/reminderSchema';
import { CallSchema } from '@/types/validationSchemas/CallSchema';
import formatPhoneForUser from '@/utils/formatPhoneForUser';
import { maskPhone } from '@/utils/maskPhone';
import { z } from 'zod';

export type LineItemDTO = z.infer<typeof LineItemSchema>;
export type OpportunityDTO = z.infer<typeof OpportunitySchema>;
export type QuoteDTO = z.infer<typeof QuoteSchema>;
export type JobDTO = z.infer<typeof JobSchema>;
export type InvoiceDTO = z.infer<typeof InvoiceSchema>;
export type ProjectDTO = z.infer<typeof ProjectSchema>;
export type Customer = z.infer<typeof customerSchema>;
export type Reminder = z.infer<typeof reminderSchema>;
export type Call = z.infer<typeof CallSchema>;
// the types of workflows
export type WorkflowType = 'opportunity' | 'quote' | 'job' | 'invoice';

// Event types for calendar event creation
export type EventType = 'job' | 'event' | 'reminder';

export type Status =
	| 'Unsaved'
	| 'Draft'
	| 'Interested'
	| 'In progress'
	| 'In communication'
	| 'Follow up'
	| 'Won'
	| 'Closed'
	| 'Sent'
	| 'Viewed'
	| 'Change requested'
	| 'Pending approval'
	| 'Approved'
	| 'Canceled'
	| 'Unscheduled'
	| 'Scheduled'
	| 'Confirmed'
	| 'Completed'
	| 'Overdue'
	| 'Paid'
	| 'Refunded'
	| 'Partially Refunded';

export type User = z.infer<typeof userSchema>;

export interface RequestedChange {
	_id: string;
	field: string;
	details: string;
	dateRequested: string;
	isCompleted: boolean;
}

export type TeamConversation = {
	_id: string;
	title: string;
	_users: string[];
	_business: string;
	_previousUsers: string[];
	_unreadUsers: string[];
	_starredUsers?: string[];
	isDirectMessage: boolean;
	attachments: any[];
	teamMessages?: any[];
	latestMessageDate: string;
	numberOfUsers: number;
	isRunner?: boolean;
	// set on channels: group chats kept in step with a location or a department
	channel?: 'location' | 'department';
	_department?: string;
	notes?: string[];
};

export type TeamMessage = {
	_id: string;
	attachments: any[];
	text: string;
	type: 'text' | 'event';
	_user: string;
	_teamConversation: string;
	reactions: any[];
	_business: string;
	updatedAt: string;
	createdAt: string;
};

export type ConversationCustomer = {
	_id: string;
	photoUrl?: string | null;
	phone: string;
	name: string;
	details: string;
	email: string | null;
	preferredChannel: string;
};

export type CustomerConversation = {
	_id: string;
	notes: any[];
	tags: string[];
	title: string;
	_business: string;
	_folder: string;
	_customer: ConversationCustomer;
	// group SMS conversations (isGroup: true) use _customers instead of _customer
	isGroup?: boolean;
	chatName?: string;
	_customers?: ConversationCustomer[];
	open: boolean;
	latestMessageDate: string;
	isRead: boolean;
	// users who have read the conversation; drives the unread indicator for users with hasOwnIsReadIndicators
	_readByUsers?: string[];
	source: string;
	_department: string | null;
	createdAt: string;
	updatedAt: string;
	__v: number;
	_assignee: string;
	_assignees: string[];
	messages: {
		_id: string;
		sender: 'customer' | 'business' | 'event' | 'ai' | 'note';
		text: string;
		_customer: string;
		_business: string;
		createdAt: string;
		updatedAt: string;
		__v: number;
		channel?: string;
		isReviewRequest?: boolean;
		SmsMessageSid?: string;
		SmsStatus?: string;
		media?: string | null;
		_user?: string;
		_customerConversation?: string;
	}[];
};

// Group-aware accessors — use these instead of touching _customer/_customers
// directly so 1:1 and group conversations render through one code path.
export const getConversationCustomers = (
	conversation: Partial<CustomerConversation> | null | undefined,
): ConversationCustomer[] => {
	if (!conversation) return [];
	if (conversation.isGroup) return conversation._customers || [];
	return conversation._customer ? [conversation._customer] : [];
};

const getCustomerLabel = (
	customer: ConversationCustomer | undefined,
	isActivated?: boolean,
): string =>
	maskPhone(customer?.name || (customer?.phone ? formatPhoneForUser(customer.phone) : '') || '', isActivated);

export const getConversationDisplayName = (
	conversation: Partial<CustomerConversation> | null | undefined,
	isActivated?: boolean,
): string => {
	if (!conversation) return '';
	if (conversation.isGroup && conversation.chatName) return conversation.chatName;
	const customers = getConversationCustomers(conversation);
	return (
		customers
			.map((c) => getCustomerLabel(c, isActivated))
			.filter(Boolean)
			.join(', ') ||
		maskPhone(conversation.title, isActivated) ||
		''
	);
};

// Joins participant names with an ampersand for the final separator:
// ["A"] -> "A", ["A","B"] -> "A & B", ["A","B","C"] -> "A, B & C".
export const formatParticipantNames = (names: string[]): string => {
	const cleaned = names.filter(Boolean);
	if (cleaned.length <= 1) return cleaned[0] || '';
	if (cleaned.length === 2) return `${cleaned[0]} & ${cleaned[1]}`;
	return `${cleaned.slice(0, -1).join(', ')} & ${cleaned[cleaned.length - 1]}`;
};

// Display name for conversation list rows. Groups use chatName when set,
// otherwise the participants ampersand-joined (e.g. "Daniel Fayle & Leah").
export const getConversationListName = (
	conversation: Partial<CustomerConversation> | null | undefined,
	isActivated?: boolean,
): string => {
	if (!conversation) return '';
	if (conversation.isGroup && conversation.chatName) return conversation.chatName;
	const customers = getConversationCustomers(conversation);
	const names = customers.map((c) => getCustomerLabel(c, isActivated)).filter(Boolean);
	return formatParticipantNames(names) || maskPhone(conversation.title, isActivated) || '';
};

export type Folder = {
	_id: string;
	title: string;
	_business: string;
};

export interface TaxLineDTO {
	_id?: string;
	name?: string;
	rate?: number;
	amount?: number;
	isDefault?: boolean;
}

// Twilio Voice SDK Call with custom parameters from TwiML
export interface TwilioCallWithParams {
	parameters?: {
		From?: string;
		To?: string;
		CallSid?: string;
		AccountSid?: string;
		[key: string]: string | undefined;
	};
	customParameters?: Map<string, string>;
}
